import { useMemo } from 'react'
import { useGetCurrentSociogram } from '@BUP/hooks/Sociogram/useGetCurrentSociogram'
import { useGetTypeSurveyConfig } from '@BUP/hooks/Sociogram/useGetTypeSurveyConfig'
import { useGetPreAnalyticData } from '@BUP/hooks/Sociogram/preAnalyticData/useGetPreAnalyticData'
import { resolveGenderQuestionId, resolveLegacyGenderCode } from '@BUP/utils/sociogramGender'

/**
 * Resolves each roster student's gender from the sociogram survey - the only place this data
 * exists (see docs/sdd-asignacion-cursos.md, "Paso 5"). Reuses the same hardcoded
 * template -> genderQuestionId mapping as SociogramDashboard/networkComponent, since that's the
 * only known-working resolution logic and there's no cleaner source to reuse instead.
 *
 * Matched by each roster row's own `grade` field, NOT the course-assignment period's `grade`
 * (period.grade is a `nivel:N` value - see openPeriod on the backend - while a sociogram
 * `survey.grade` is a per-course `grade:<uuid>` tag; those two never coincide, which is exactly
 * why this always resolved zero surveys before). `listOrganizationRoster` (backend) already
 * computes each student's real `grade:<uuid>` tag for this reason - see its own doc comment.
 *
 * KNOWN LIMITATION: this resolves a single survey from the first roster row that has a `grade`,
 * so a period spanning more than one course/section of the same nivel (each with its own
 * `grade:<uuid>` and its own sociogram survey) only gets gender data from one of them - the rest
 * of the roster's gender column stays blank, same as today's "unknown template" fallback. Fixing
 * that fully needs resolving+merging one survey per distinct roster grade, not just one.
 */
export const useSociogramGenderExport = (roster) => {
  const { dataCurrentSociogram, isLoadingCurrentSociogram, errorCurrentSociogram } = useGetCurrentSociogram()

  const survey = useMemo(() => {
    const surveys = dataCurrentSociogram?.currentSociogram?.surveys
    const rosterGrade = roster?.find(student => student.grade)?.grade
    return rosterGrade && Array.isArray(surveys) ? surveys.find(candidate => candidate.grade === rosterGrade) : null
  }, [dataCurrentSociogram, roster])

  const {
    data: typeSurveyConfig,
    loading: isLoadingTemplate,
    error: errorTemplate
  } = useGetTypeSurveyConfig(survey?.id)
  const templateName = typeSurveyConfig?.[0]?.survey_template?.name
  const genderQuestionId = resolveGenderQuestionId(templateName)

  const {
    preAnalyticData,
    isLoadingPreAnalyticData,
    errorPreAnalyticData
  } = useGetPreAnalyticData(survey?.id)

  const genderByEnrollmentId = useMemo(() => {
    const content = preAnalyticData?.content
    const map = new Map()
    if (!genderQuestionId || !Array.isArray(content)) return map
    content
      .filter(entry => entry.question_id === genderQuestionId)
      .forEach(entry => map.set(entry.enrollmentId, resolveLegacyGenderCode(entry.answerId)))
    return map
  }, [preAnalyticData, genderQuestionId])

  return {
    isLoading: isLoadingCurrentSociogram || (!!survey?.id && (isLoadingTemplate || isLoadingPreAnalyticData)),
    error: errorCurrentSociogram || errorTemplate || errorPreAnalyticData,
    hasSurvey: !!survey,
    hasKnownTemplate: !!genderQuestionId,
    getGenderFor: (enrollmentId) => genderByEnrollmentId.get(enrollmentId) || ''
  }
}
